<template>
  <b-overlay :show="loading || workersLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <transition name="bounce">
      <b-container
        v-if="isPlainTradie"
        class="pt-5 mt-5 mb-5 custom-container container schedule-page"
      >
        <div class="schedule-page-header">
          <h4 class="schedule-page-title">Schedule</h4>
        </div>
        <my-jobs-list />
      </b-container>

      <b-container
        v-else
        class="pt-5 mt-5 mb-5 custom-container container schedule-page"
      >
        <div class="schedule-page-header">
          <h4 class="schedule-page-title">Schedule</h4>
          <b-button
            v-if="canManage"
            variant="primary"
            class="schedule-new-job-btn"
            @click="openCreateModal"
          >
            <b-icon-plus class="mr-1" /> New Job
          </b-button>
        </div>

        <b-card
          border-variant="light"
          bg-variant="light"
          body-class="p-0"
          class="schedule-board"
        >
          <WeekNavHeader
            :week-label="weekLabel"
            :project-options="projectOptions"
            :value="projectFilter"
            :search="searchQuery"
            :start-day="startDay"
            @input="onProjectFilterChange"
            @search="onSearchChange"
            @prev="prevWeek"
            @next="nextWeek"
            @today="goToday"
            @start-day-change="onStartDayChange"
          />

          <WeekRosterGrid
            :workers="paginatedWorkers"
            :week-dates="weekDatesList"
            :jobs-by-worker-and-date="jobsByWorkerAndDate"
            :can-manage="canManage"
            @cell-click="onCellClick"
            @chip-click="onChipClick"
            @chip-make-repeat="onChipMakeRepeat"
            @chip-action-done="onChipActionDone"
          />

          <b-pagination
            v-if="filteredWorkers.length > workerPerPage"
            v-model="workerPage"
            :total-rows="filteredWorkers.length"
            :per-page="workerPerPage"
            align="center"
            class="schedule-pagination"
          />
        </b-card>
      </b-container>
    </transition>

    <template v-if="!isPlainTradie">
      <job-type-chooser ref="jobTypeChooser" @choose="onTypeChosen" />

      <job-form-modal
        ref="jobFormModal"
        :project-options="projectOptionsForForm"
        @saved="onJobSaved"
      />
    </template>
  </b-overlay>
</template>

<script>
import { mapGetters } from 'vuex'
import JobFormModal from '@/components/schedule/JobFormModal'
import JobTypeChooser from '@/components/schedule/JobTypeChooser.vue'
import WeekNavHeader from '@/components/schedule/WeekNavHeader'
import WeekRosterGrid from '@/components/schedule/WeekRosterGrid'
import MyJobsList from '@/components/schedule/MyJobsList.vue'
import {
  mondayOf,
  startDayOf,
  settingsStartDay,
  weekDates,
  addWeeks,
  formatWeekLabel,
} from '@/utils/weekHelpers'

export default {
  name: 'SchedulePage',

  components: {
    JobFormModal,
    JobTypeChooser,
    WeekNavHeader,
    WeekRosterGrid,
    MyJobsList,
  },

  data() {
    return {
      loading: false,
      projectFilter: null,
      workersLoading: false,
      weekStart: mondayOf(),
      startDay: 1,
      workers: [],
      searchQuery: '',
      workerPage: 1,
      workerPerPage: 10,
    }
  },

  computed: {
    ...mapGetters('auth', ['user', 'hasPermission']),
    ...mapGetters('schedule', ['jobs', 'jobsByWorkerAndDate']),
    ...mapGetters('project', ['projects']),

    canManage() {
      return this.hasPermission('schedule.manage')
    },

    isTradieUser() {
      return this.user ? this.user.user_type === 'tradie' : false
    },

    isSupervisorTradie() {
      return this.isTradieUser && !!this.user.is_supervisor
    },

    // A plain (non-supervisor) tradie has no schedule.view/manage permission
    // and can't see the admin roster board — it spans every worker in the
    // company. They get their own jobs only, via the tradie-facing API.
    isPlainTradie() {
      return this.isTradieUser && !this.isSupervisorTradie
    },

    projectOptions() {
      return [
        { value: null, text: 'All Projects' },
        ...this.projects
          .filter((p) => !p.is_archived)
          .map((p) => ({ value: p.id, text: p.name })),
      ]
    },

    projectOptionsForForm() {
      return this.projects
        .filter((p) => !p.is_archived)
        .map((p) => ({ value: p.id, text: p.name }))
    },

    weekDatesList() {
      return weekDates(this.weekStart)
    },

    weekLabel() {
      return formatWeekLabel(this.weekStart)
    },

    filteredWorkers() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return this.workers
      return this.workers.filter((w) =>
        (w.full_name || '').toLowerCase().includes(query),
      )
    },

    paginatedWorkers() {
      const start = (this.workerPage - 1) * this.workerPerPage
      return this.filteredWorkers.slice(start, start + this.workerPerPage)
    },
  },

  async created() {
    // A plain tradie has no schedule.view/manage permission — none of the
    // admin-only endpoints below would succeed for them, and MyJobsList
    // fetches its own data via the tradie-facing API instead.
    if (this.isPlainTradie) return

    this.$store.dispatch('project/getProjects', {
      page: 1,
      limit: 200,
      is_archived: false,
    })
    this.fetchWorkers()
    // Week start precedence: server pay-period setting first, then the local
    // override, then Monday. The dispatch is cached, so this is cheap.
    let startDay = null
    const settings = await this.$store.dispatch(
      'timesheet/getTimesheetSettings',
    )
    if (settings) startDay = settingsStartDay(settings)
    if (startDay === null) {
      try {
        const saved = localStorage.getItem('tp_week_start_day')
        if (saved !== null) {
          const parsed = parseInt(saved, 10)
          if (!isNaN(parsed) && parsed >= 0 && parsed <= 6) {
            startDay = parsed
          }
        }
      } catch (e) {}
    }
    if (startDay !== null) {
      this.startDay = startDay
      this.weekStart = startDayOf(null, startDay)
    }
    this.fetchJobs()
  },

  methods: {
    async fetchJobs() {
      this.loading = true
      try {
        await this.$store.dispatch('schedule/getJobs', {
          project_id: this.projectFilter || undefined,
          date_from: this.weekDatesList[0],
          date_to: this.weekDatesList[6],
        })
      } catch (e) {
        this.$bvToast.toast('Failed to load scheduled jobs.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.loading = false
      }
    },

    // Extending a series creates jobs beyond the current end date, which is
    // almost always outside the currently-viewed week — a plain fetchJobs()
    // re-fetches that same visible week and the new jobs never render. When
    // the save carries a jumpToDate, move the view to that week first so
    // the BA actually sees the result instead of a screen that looks
    // unchanged despite the save having succeeded.
    onJobSaved(payload) {
      const jumpToDate = payload && payload.jumpToDate
      if (jumpToDate) {
        this.weekStart = startDayOf(jumpToDate, this.startDay)
      }
      this.fetchJobs()
    },

    async fetchWorkers() {
      this.workersLoading = true
      try {
        this.workers = await this.$store.dispatch(
          'schedule/getAvailableWorkers',
          {
            project_id: this.projectFilter || undefined,
          },
        )
      } catch (e) {
        this.$bvToast.toast('Failed to load workers.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.workersLoading = false
      }
    },

    onProjectFilterChange(value) {
      this.projectFilter = value
      this.workerPage = 1
      this.fetchJobs()
      this.fetchWorkers()
    },

    onSearchChange(value) {
      this.searchQuery = value
      this.workerPage = 1
    },

    prevWeek() {
      this.weekStart = addWeeks(this.weekStart, -1)
      this.fetchJobs()
    },

    nextWeek() {
      this.weekStart = addWeeks(this.weekStart, 1)
      this.fetchJobs()
    },

    goToday() {
      this.weekStart = startDayOf(null, this.startDay)
      this.fetchJobs()
    },

    onStartDayChange(val) {
      this.startDay = val
      try {
        localStorage.setItem('tp_week_start_day', String(val))
      } catch (e) {}
      this.weekStart = startDayOf(this.weekStart, val)
      this.fetchJobs()
    },

    openCreateModal() {
      this.$refs.jobTypeChooser.open()
    },

    // An empty cell knows the date and the worker — both ride through the
    // chooser into whichever form shape the BA picks.
    onCellClick({ workerId, date }) {
      const worker = this.workers.find((w) => w.id === workerId)
      this.$refs.jobTypeChooser.open(
        { date, worker_ids: [workerId] },
        worker ? worker.full_name : '',
      )
    },

    onTypeChosen({ mode, prefill }) {
      this.$refs.jobFormModal.openCreate(prefill, mode)
    },

    onChipClick({ job }) {
      this.$refs.jobFormModal.openEdit(job)
    },

    // ⋯ → "Make this repeat…" from the grid: open the job, then switch it
    // into repeat mode so the BA lands on the pattern they just asked for.
    async onChipMakeRepeat({ job }) {
      this.$refs.jobFormModal.openEdit(job)
      await this.$nextTick()
      this.$refs.jobFormModal.enterRepeatMode()
    },

    // Every chip action runs inside JobActionsMenu (confirm, request, toast).
    // The page only refreshes, or opens the form when the API asked for a new
    // date (a past-dated reopen).
    async onChipActionDone({ changed, dateError, job }) {
      if (dateError) {
        this.$refs.jobFormModal.openReopen(job)
        await this.$nextTick()
        this.$refs.jobFormModal.setDateError(dateError)
        return
      }
      if (changed) this.fetchJobs()
    },
  },
}
</script>

<style scoped>
.schedule-page {
  max-width: 1440px;
}

.schedule-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.5rem 0 1rem;
}

.schedule-page-title {
  margin: 0;
  color: #111827;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.schedule-new-job-btn {
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  font-weight: 600;
  box-shadow: 0 6px 14px rgba(0, 123, 255, 0.16);
}

.schedule-board {
  border: 1px solid #e5e7eb !important;
  border-radius: 12px;
  /* Not overflow:hidden — it clipped the chip's ⋯ menu near the bottom row.
     The children that need the rounded corner clip themselves. */
  overflow: visible;
  background: #ffffff !important;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
}

.schedule-pagination {
  margin: 0;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
}

@media (max-width: 575px) {
  .schedule-page-header {
    align-items: stretch;
    flex-direction: column;
  }

  .schedule-new-job-btn {
    justify-content: center;
  }
}
</style>
